"use client";
import { Fragment, useState } from "react";
import { hasConsented, writeConsent } from "../services/consent.service";
import { FONT_MONO } from "theme/fonts";
import { useConsentGateText } from "./Consentgate.text";

/* ── DECLINED SCREEN ── */
function DeclinedScreen({ onReconsider }: { onReconsider: () => void }) {
  const t = useConsentGateText().declined;
  return (
    <div style={styles.overlay}>
      <div style={{ ...styles.card, maxWidth: 420, textAlign: "center" }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>👋</div>
        <h2 style={styles.title}>{t.title}</h2>
        <p style={styles.bodyText}>{t.body}</p>
        <button onClick={onReconsider} style={styles.primaryBtn}>
          {t.again}
        </button>
      </div>
    </div>
  );
}

/* ── DISCLAIMER MODAL ── */
function DisclaimerModal({
  onAccept,
  onDecline,
}: {
  onAccept: () => void;
  onDecline: () => void;
}) {
  const [checked, setChecked] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const t = useConsentGateText();

  return (
    <div style={styles.overlay}>
      <div style={styles.card}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <span style={{ fontSize: 26 }}>✨</span>
          <h2 style={styles.title}>{t.title}</h2>
        </div>

        {t.intro.map((para, i) => (
          <p key={i} style={i === 0 ? { ...styles.bodyText, marginTop: 0 } : styles.bodyText}>{para}</p>
        ))}

        <button
          onClick={() => setShowDetails((v) => !v)}
          style={styles.linkBtn}
          type="button"
        >
          {showDetails ? t.hideDetails : t.showDetails}
        </button>

        {showDetails && (
          <div style={styles.detailsBody}>
            {t.details.map((d) => (
              <Section key={d.title} title={d.title}>
                {d.body.map((s, i) => (s.b ? <Strong key={i}>{s.t}</Strong> : <Fragment key={i}>{s.t}</Fragment>))}
              </Section>
            ))}
          </div>
        )}

        <label style={styles.checkboxRow}>
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            style={styles.checkbox}
          />
          <span style={{ fontSize: 12.5, color: "#cfe3f5", lineHeight: 1.4 }}>
            {t.checkbox}
          </span>
        </label>

        <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
          <button onClick={onDecline} style={styles.secondaryBtn}>
            {t.decline}
          </button>
          <button
            onClick={onAccept}
            disabled={!checked}
            style={{
              ...styles.primaryBtn,
              flex: 1,
              opacity: checked ? 1 : 0.4,
              cursor: checked ? "pointer" : "not-allowed",
            }}
          >
            {t.accept}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={styles.sectionTitle}>{title}</div>
      <div style={styles.sectionBody}>{children}</div>
    </div>
  );
}

function Strong({ children }: { children: React.ReactNode }) {
  return <strong style={{ color: "#e2e8f0", fontWeight: 700 }}>{children}</strong>;
}

/* ── PUBLIC WRAPPER ── */
export default function ConsentGate({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<"accepted" | "prompt" | "declined">(() =>
    hasConsented() ? "accepted" : "prompt"
  );

  const handleAccept = () => { writeConsent(); setStatus("accepted"); };
  const handleDecline = () => setStatus("declined");
  const handleReconsider = () => setStatus("prompt");

  if (status === "declined") return <DeclinedScreen onReconsider={handleReconsider} />;

  if (status === "prompt") {
    return (
      <>
        {children}
        <DisclaimerModal onAccept={handleAccept} onDecline={handleDecline} />
      </>
    );
  }

  return <>{children}</>;
}

/* ── STYLES ── */
const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: "fixed", inset: 0, zIndex: 2000,
    background: "rgba(3,7,18,0.92)", backdropFilter: "blur(6px)",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: 16, fontFamily: FONT_MONO,
  },
  card: {
    background: "#0b1628", border: "1px solid #1e3a5f", borderRadius: 18,
    width: "min(460px, 96vw)", maxHeight: "90vh", overflowY: "auto",
    padding: "22px 22px 18px", display: "flex", flexDirection: "column",
    boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
    scrollbarWidth: "thin", scrollbarColor: "#1e3a5f transparent",
  },
  title: { fontSize: 18, fontWeight: 800, color: "#e2e8f0", margin: 0 },
  bodyText: { fontSize: 13, color: "#a8bdd1", lineHeight: 1.55, margin: "8px 0" },
  linkBtn: {
    alignSelf: "flex-start", background: "none", border: "none",
    color: "#38bdf8", fontSize: 12, fontWeight: 700, cursor: "pointer",
    padding: "4px 0", marginTop: 2,
  },
  detailsBody: {
    background: "#07101e", border: "1px solid #1e293b",
    borderRadius: 12, padding: "14px 16px", marginTop: 8,
  },
  sectionTitle: {
    fontSize: 10.5, fontWeight: 800, color: "#38bdf8",
    letterSpacing: 0.5, textTransform: "uppercase" as const, marginBottom: 5,
  },
  sectionBody: { fontSize: 12, color: "#a8bdd1", lineHeight: 1.55 },
  checkboxRow: { display: "flex", alignItems: "flex-start", gap: 9, marginTop: 16, cursor: "pointer" },
  checkbox: { marginTop: 2, width: 16, height: 16, accentColor: "#38bdf8", cursor: "pointer", flexShrink: 0 },
  primaryBtn: {
    background: "#38bdf8", color: "#031018", border: "none",
    borderRadius: 10, padding: "11px 18px", fontSize: 13, fontWeight: 800, cursor: "pointer",
  },
  secondaryBtn: {
    background: "transparent", color: "#94a3b8", border: "1px solid #334155",
    borderRadius: 10, padding: "11px 16px", fontSize: 13, fontWeight: 700,
    cursor: "pointer", whiteSpace: "nowrap" as const,
  },
};